import ApexCharts from 'apexcharts'

// The chart in account details: what the account is for, drawn over time. The data comes with the partial
// (data-account-insights), the modal may open on any page, so this lives globally.
const COLOR_MAIN_LIGHT = '#556ee6';
const COLOR_MAIN_DARK = '#6b82ea';
const COLOR_DEBT = '#c2700a';
const COLOR_PENDING = '#a6b0cf';

const charts = new Map();

const isDark = () => document.documentElement.getAttribute('data-bs-theme') === 'dark'
    || document.body.getAttribute('data-layout-mode') === 'dark';

const toTime = date => {
    const [year, month, day] = date.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
};

function options(holder, data) {
    const dark = isDark();
    const styles = getComputedStyle(document.body);
    const text = styles.getPropertyValue('--bs-body-color').trim() || (dark ? '#eff2f7' : '#495057');
    const grid = styles.getPropertyValue('--bs-border-color').trim() || (dark ? '#32394e' : '#eff2f7');
    const locale = document.documentElement.lang || undefined;
    const main = data.tone === 'debt' ? COLOR_DEBT : (dark ? COLOR_MAIN_DARK : COLOR_MAIN_LIGHT);
    const money = value => `${Number(value).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${data.currency}`;
    const axis = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format;
    const isBar = data.type === 'bar';

    // bars get their labels as categories, so the dates are not parsed and reformatted
    const series = data.series.map(item => ({
        name: item.name,
        data: item.data.map(([date, value]) => isBar ? value : [toTime(date), value])
    }));
    // one format per axis: days within a year, months over longer spans
    const times = data.series.flatMap(item => item.data.map(([date]) => toTime(date)));
    const dateFormat = Math.max(...times) - Math.min(...times) > 400 * 86400000 ? 'MM/yy' : 'dd.MM';
    const categories = isBar ? data.series[0].data.map(([label]) => label) : undefined;
    const colors = data.series.map(item => item.pending || item.dashed ? COLOR_PENDING : main);

    return {
        chart: {
            type: isBar ? 'bar' : data.type,
            height: 220,
            stacked: isBar,
            foreColor: text,
            background: 'transparent',
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: { enabled: false }
        },
        theme: { mode: dark ? 'dark' : 'light' },
        series,
        colors,
        stroke: isBar ? { show: false } : { width: 2, curve: 'straight', dashArray: data.series.map(item => item.dashed ? 5 : 0) },
        fill: data.type === 'area' ? { type: 'solid', opacity: data.series.map(item => item.dashed ? 0 : 0.15) } : { opacity: 1 },
        markers: { size: 0, hover: { size: 5 } },
        plotOptions: { bar: { columnWidth: '60%', borderRadius: 4, borderRadiusApplication: 'end' } },
        dataLabels: { enabled: false },
        xaxis: isBar
            ? { type: 'category', categories, labels: { rotate: 0, hideOverlappingLabels: true } }
            : {
                type: 'datetime',
                labels: { datetimeUTC: true, datetimeFormatter: { year: 'yyyy', month: dateFormat, day: dateFormat } },
                tooltip: { enabled: false }
            },
        yaxis: { labels: { formatter: axis }, min: typeof data.min === 'number' ? data.min : undefined, forceNiceScale: true },
        grid: { borderColor: grid, strokeDashArray: 3 },
        legend: { show: data.series.length > 1, position: 'bottom' },
        annotations: data.target ? {
            yaxis: [{
                y: data.target.y,
                borderColor: text,
                strokeDashArray: 4,
                label: { text: data.target.label, borderWidth: 0, style: { background: 'transparent', color: text } }
            }]
        } : {},
        tooltip: {
            theme: dark ? 'dark' : 'light',
            shared: !isBar,
            intersect: false,
            x: { format: 'dd.MM.yyyy' },
            y: { formatter: value => value === null || value === undefined ? '' : money(value) }
        }
    };
}

function render(holder) {
    if (!holder.isConnected || holder.offsetWidth === 0) {
        return;
    }
    charts.get(holder)?.destroy();
    const data = JSON.parse(holder.dataset.accountInsights);
    const chart = new ApexCharts(holder, options(holder, data));
    charts.set(holder, chart);
    chart.render();
}

export function initAccountInsights(scope = document) {
    for (const [holder, chart] of charts) {
        if (!holder.isConnected) {
            chart.destroy();
            charts.delete(holder);
        }
    }
    scope.querySelectorAll('[data-account-insights]').forEach(holder => {
        if (!charts.has(holder)) {
            render(holder);
        }
    });
}

document.addEventListener('shown.bs.modal', event => initAccountInsights(event.target));
window.addEventListener('app:theme-changed', () => charts.forEach((chart, holder) => render(holder)));
window.initializeAccountInsights = initAccountInsights;
